import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost } from '../../../api/client';
import { PageHeader } from '../../../components/common/PageHeader';
import { SelectField } from '../../../components/fields/SelectField';
import { PermissionGate } from '../../../components/auth/PermissionGate';
import { useAuthStore } from '../../../store/auth.store';
import {
  TRANSFER_STATUS_COLOR, TRANSFER_STATUS_LABEL, TransferAction, availableActions, dispatchError, receiveError, receiveHint, requestError, returnError,
} from './transferLogic';

// Branch Cash P4 - inter-branch cash transfers: request -> dispatch (cash leaves the sender, in transit) -> receive (cash enters the
// destination only now, for what actually arrived) -> resolve a discrepancy. Buttons follow permissions and branch grants, but the
// server is the authority for every one of them.

const unwrap = (d: any) => d?.data ?? d;
const errMsg = (e: any, fallback: string) => e?.response?.data?.error ?? fallback;
const rupees = (n: number | null | undefined) => (n === null || n === undefined ? '-' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const when = (d: string | null | undefined) => (d ? new Date(d).toLocaleString() : '-');

function RequestForm({ branches }: { branches: any[] }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const sendable = branches.filter((b) => b.granted);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const error = requestError({ from, to, amount, reason });
  const save = useMutation({
    mutationFn: () => apiPost('/branch-cash/transfers', { fromBranchId: from, toBranchId: to, amount: amount.trim(), reason: reason.trim(), referenceNo: referenceNo.trim() || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bc-transfers'] }); setAmount(''); setReason(''); setReferenceNo(''); enqueueSnackbar('Transfer requested - no cash has moved yet', { variant: 'success' }); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'Could not request the transfer'), { variant: 'error' }),
  });
  const label = (b: any) => `${b.branchCode} - ${b.name}`;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Request a cash transfer</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5 }}>
        <SelectField label="From (sending branch)" value={from} onChange={(e) => setFrom(e.target.value)} options={sendable.map((b) => ({ value: b.id, label: label(b) }))} />
        <SelectField label="To (receiving branch)" value={to} onChange={(e) => setTo(e.target.value)} options={branches.filter((b) => b.id !== from).map((b) => ({ value: b.id, label: label(b) }))} />
        <TextField label="Amount (₹)" value={amount} onChange={(e) => setAmount(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Reference" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} InputLabelProps={{ shrink: true }} />
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
        <Button variant="contained" disabled={!!error || save.isPending} onClick={() => save.mutate()}>Request transfer</Button>
        {error && <Typography variant="caption" color="text.secondary">{error}</Typography>}
      </Stack>
    </Paper>
  );
}

function ActionDialog({ action, transfer, onClose }: { action: TransferAction; transfer: any; onClose: () => void }) {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const [amount, setAmount] = useState(action === 'receive' || action === 'return' ? String(transfer.dispatchedAmount ?? '') : action === 'dispatch' ? String(transfer.amount) : '');
  const [text, setText] = useState('');
  const dispatched = Number(transfer.dispatchedAmount ?? 0);
  const hint = action === 'receive' ? receiveHint(dispatched, amount) : null;
  const error = action === 'dispatch' ? dispatchError(transfer.amount, amount)
    : action === 'receive' ? receiveError(dispatched, amount, text)
      : action === 'return' ? returnError(dispatched, amount, text)
        : !text.trim() ? (action === 'resolve' ? 'A resolution note is required' : 'A reason is required') : '';
  const run = useMutation({
    mutationFn: () => {
      const base = `/branch-cash/transfers/${transfer.id}`;
      if (action === 'dispatch') return apiPost(`${base}/dispatch`, { amount: amount.trim() || undefined });
      if (action === 'receive') return apiPost(`${base}/receive`, { receivedAmount: amount.trim(), explanation: text.trim() || undefined });
      if (action === 'resolve') return apiPost(`${base}/resolve`, { note: text.trim() });
      if (action === 'return') return apiPost(`${base}/return`, { returnedAmount: amount.trim(), reason: text.trim() });
      return apiPost(`${base}/cancel`, { reason: text.trim() });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bc-transfers'] }); qc.invalidateQueries({ queryKey: ['bc-current'] }); enqueueSnackbar('Done', { variant: 'success' }); onClose(); },
    onError: (e: any) => enqueueSnackbar(errMsg(e, 'The server refused this action'), { variant: 'error' }),
  });
  const titles: Record<TransferAction, string> = { dispatch: 'Dispatch cash', receive: 'Confirm receipt', resolve: 'Resolve discrepancy', cancel: 'Cancel request', return: 'Take cash back' };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{titles[action]} - {transfer.transferNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {action === 'dispatch' && <Alert severity="info">The cash leaves the sending branch's balance now and is in transit until the other branch confirms it.</Alert>}
          {action === 'receive' && <Alert severity="info">Enter what was actually counted. The receiving branch's cash increases only by this amount.</Alert>}
          {action === 'return' && <Alert severity="info">Use this only when the cash never reached the other branch and is physically back at the sending branch. It must be the full dispatched amount, confirmed by someone other than the person who sent it; the transfer can no longer be received afterwards.</Alert>}
          {(action === 'dispatch' || action === 'receive' || action === 'return') && (
            <TextField autoFocus label={action === 'dispatch' ? `Amount to send (requested ${rupees(transfer.amount)})` : action === 'return' ? `Amount taken back (sent ${rupees(dispatched)})` : `Amount received (sent ${rupees(dispatched)})`}
              value={amount} onChange={(e) => setAmount(e.target.value)} InputLabelProps={{ shrink: true }} />
          )}
          {hint?.valid && hint.needsExplanation && <Alert severity="warning">Difference {rupees(hint.discrepancy)} - this will be recorded as a discrepancy.</Alert>}
          {(action === 'resolve' || action === 'cancel' || action === 'return' || (action === 'receive' && hint?.needsExplanation)) && (
            <TextField autoFocus={action === 'resolve' || action === 'cancel'} multiline minRows={2} InputLabelProps={{ shrink: true }}
              label={action === 'resolve' ? 'Resolution note (required)' : action === 'cancel' || action === 'return' ? 'Reason (required)' : 'Explanation (required)'}
              value={text} onChange={(e) => setText(e.target.value)} />
          )}
          {error && <Typography variant="caption" color="text.secondary">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={!!error || run.isPending} onClick={() => run.mutate()}>Confirm</Button>
      </DialogActions>
    </Dialog>
  );
}

export function TransfersPage() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const me = useAuthStore((s) => s.user?.id);
  const [status, setStatus] = useState('');
  const [branchId, setBranchId] = useState('');
  const [active, setActive] = useState<{ action: TransferAction; transfer: any } | null>(null);

  const branchesQ = useQuery({ queryKey: ['bc-transfer-branches'], queryFn: async () => unwrap(await apiGet('/branch-cash/transfers/branches')) as any[] });
  const branches = branchesQ.data ?? [];
  const nameOf = useMemo(() => new Map(branches.map((b) => [b.id, `${b.branchCode} - ${b.name}`])), [branches]);
  const grantedIds = useMemo(() => new Set(branches.filter((b) => b.granted).map((b) => b.id)), [branches]);

  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (branchId) params.set('branchId', branchId);
  const listQ = useQuery({ queryKey: ['bc-transfers', 'list', status, branchId], queryFn: async () => unwrap(await apiGet(`/branch-cash/transfers?${params.toString()}`)) as any });
  const transitQ = useQuery({ queryKey: ['bc-transfers', 'transit'], queryFn: async () => unwrap(await apiGet('/branch-cash/transfers/in-transit')) as any });
  const rows: any[] = listQ.data?.rows ?? [];
  const transit = transitQ.data;

  return (
    <Box>
      <PageHeader title="Cash Transfers" subtitle="Move cash between branches - it is in neither balance while in transit" />
      {(listQ.isError || branchesQ.isError) && <Alert severity="error" sx={{ mb: 2 }}>{errMsg(listQ.error ?? branchesQ.error, 'Could not load transfers')}</Alert>}

      <PermissionGate permission="cash:transfer_create"><RequestForm branches={branches} /></PermissionGate>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary">In transit now</Typography>
          <Typography variant="h6">{rupees(transit?.totalInTransit ?? 0)}</Typography>
          <Typography variant="caption">{transit?.count ?? 0} transfer(s)</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary">Overdue</Typography>
          <Typography variant="h6" color={transit?.overdueCount ? 'error' : undefined}>{rupees(transit?.overdueAmount ?? 0)}</Typography>
          <Typography variant="caption">{transit?.overdueCount ?? 0} transfer(s)</Typography>
        </Paper>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, maxWidth: 520 }}>
        <SelectField label="Branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}
          options={[{ value: '', label: 'All my branches' }, ...branches.filter((b) => b.granted).map((b) => ({ value: b.id, label: `${b.branchCode} - ${b.name}` }))]} />
        <SelectField label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
          options={[{ value: '', label: 'All' }, ...Object.entries(TRANSFER_STATUS_LABEL).map(([value, label]) => ({ value, label }))]} />
      </Stack>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell><TableCell>From</TableCell><TableCell>To</TableCell>
              <TableCell align="right">Requested</TableCell><TableCell align="right">Sent</TableCell><TableCell align="right">Received</TableCell>
              <TableCell>Status</TableCell><TableCell>Requested at</TableCell><TableCell>Reason</TableCell><TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((t) => {
              const actions = availableActions(t, hasPermission, (id) => grantedIds.has(id), me);
              return (
                <TableRow key={t.id} hover>
                  <TableCell>{t.transferNumber}</TableCell>
                  <TableCell>{nameOf.get(t.fromBranchId) ?? t.fromBranchId}</TableCell>
                  <TableCell>{nameOf.get(t.toBranchId) ?? t.toBranchId}</TableCell>
                  <TableCell align="right">{rupees(t.amount)}</TableCell>
                  <TableCell align="right">{rupees(t.dispatchedAmount)}</TableCell>
                  <TableCell align="right">{rupees(t.receivedAmount)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={TRANSFER_STATUS_LABEL[t.status] ?? t.status} color={TRANSFER_STATUS_COLOR[t.status] ?? 'default'} />
                    {t.isOverdue && <Chip size="small" label="Overdue" color="warning" sx={{ ml: 0.5 }} />}
                    {t.discrepancyAmount ? <Typography variant="caption" display="block">{rupees(t.discrepancyAmount)}: {t.discrepancyReason}</Typography> : null}
                    {t.status === 'RETURNED' ? <Typography variant="caption" display="block">{rupees(t.returnedAmount)} back at sender: {t.returnReason}</Typography> : null}
                  </TableCell>
                  <TableCell>{when(t.requestedAt)}</TableCell>
                  <TableCell>{t.reason}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {actions.map((a) => (
                        <Button key={a} size="small" variant={a === 'cancel' ? 'text' : 'outlined'} color={a === 'cancel' ? 'inherit' : 'primary'} onClick={() => setActive({ action: a, transfer: t })}>
                          {a === 'dispatch' ? 'Dispatch' : a === 'receive' ? 'Receive' : a === 'resolve' ? 'Resolve' : a === 'return' ? 'Return' : 'Cancel'}
                        </Button>
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={10}><Typography variant="body2" color="text.secondary">No transfers.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      {active && <ActionDialog action={active.action} transfer={active.transfer} onClose={() => setActive(null)} />}
    </Box>
  );
}

export default TransfersPage;
